import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FiArrowRight, FiChevronLeft, FiChevronRight, FiStar } from 'react-icons/fi';
import PlaceholderImage from './PlaceholderImage';
import { webpVariant } from '../utils/img';
import { useLanguage } from '../context/LanguageContext';

// How many cards are visible per "page" depending on viewport width.
const perViewForWidth = (w) => {
  if (w < 640) return 1;
  if (w < 980) return 2;
  if (w < 1280) return 3;
  return 4;
};

// ─── Single featured card: image in risalto, hover CTA "Scopri" ───
const FeaturedCard = ({ product }) => {
  const [imgError, setImgError] = useState(false);
  const { loc, lang, t } = useLanguage();
  const en = lang === 'en';
  const hasImage = product.image && !imgError;

  return (
    <Link to={`/prodotto/${product.slug}`} className="feat-card">
      <div className="feat-card-media">
        {hasImage ? (
          <picture>
            {webpVariant(product.image) && <source srcSet={webpVariant(product.image)} type="image/webp" />}
            <img src={product.image} alt={loc(product, 'name')} loading="lazy" decoding="async"
              onError={() => setImgError(true)} />
          </picture>
        ) : (
          <PlaceholderImage name={product.name} type="product" />
        )}
        <div className="feat-card-shine" aria-hidden="true" />
        <div className="feat-card-badges">
          {product.is_new === 1 && <span className="feat-badge new">{t('product.new')}</span>}
          {(product.compostable === 1 || product.compostable === '1') && (
            <span className="feat-badge eco">{t('product.eco')}</span>
          )}
        </div>
        <div className="feat-card-overlay">
          <span className="feat-card-cta">
            {t('featuredSection.discover')} <FiArrowRight size={16} />
          </span>
        </div>
      </div>
      <div className="feat-card-body">
        {product.category_name && (
          <span className="feat-card-cat">{loc(product, 'category_name')}</span>
        )}
        <h3>{loc(product, 'name')}</h3>
        <p>{loc(product, 'short_description') || loc(product, 'description')}</p>
        <span className="feat-card-link">
          {en ? 'Discover' : 'Scopri'} <FiArrowRight size={14} />
        </span>
      </div>
    </Link>
  );
};

const FeaturedProducts = () => {
  const { t } = useLanguage();
  const [config, setConfig] = useState(null);
  const [perView, setPerView] = useState(() =>
    perViewForWidth(typeof window !== 'undefined' ? window.innerWidth : 1280));
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => {
    let mounted = true;
    import('../api').then(({ default: api }) => {
      api.getFeaturedConfig()
        .then((res) => { if (mounted) setConfig(res.data); })
        .catch(() => { if (mounted) setConfig({ enabled: false, products: [] }); });
    });
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    const onResize = () => setPerView(perViewForWidth(window.innerWidth));
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const products = (config && config.products) || [];
  const carousel = config && config.carousel_enabled && products.length > perView;
  const maxIndex = Math.max(0, products.length - perView);

  // Clamp index when perView / product count changes.
  useEffect(() => {
    setIndex((i) => Math.min(i, Math.max(0, products.length - perView)));
  }, [perView, products.length]);

  const advance = useCallback(() => {
    setIndex((i) => (i >= maxIndex ? 0 : i + 1));
  }, [maxIndex]);

  // Autoplay driven by the admin-configured speed (seconds per slide).
  useEffect(() => {
    if (!carousel || paused) return undefined;
    const speedMs = Math.max(1, Number(config.carousel_speed) || 3) * 1000;
    timerRef.current = setInterval(advance, speedMs);
    return () => clearInterval(timerRef.current);
  }, [carousel, paused, config, advance]);

  if (!config || !config.enabled || products.length === 0) return null;

  const goPrev = () => setIndex((i) => (i <= 0 ? maxIndex : i - 1));
  const goNext = () => setIndex((i) => (i >= maxIndex ? 0 : i + 1));
  const pageCount = maxIndex + 1;

  return (
    <section className="feat-section" aria-label={t('featuredSection.title')}>
      <motion.div className="feat-header"
        initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }} transition={{ duration: 0.7 }}>
        <span className="feat-eyebrow"><FiStar size={13} /> {t('featuredSection.badge')}</span>
        <h2 className="feat-title">{t('featuredSection.title')}</h2>
        <p className="feat-subtitle">{t('featuredSection.subtitle')}</p>
      </motion.div>

      {carousel ? (
        <div className="feat-carousel"
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}>
          <button className="feat-nav prev" onClick={goPrev} aria-label={t('featuredSection.prev')}>
            <FiChevronLeft size={22} />
          </button>
          <div className="feat-viewport">
            <div className="feat-track"
              style={{
                transform: `translateX(-${index * (100 / perView)}%)`,
                transition: 'transform 0.6s cubic-bezier(0.22, 1, 0.36, 1)',
              }}>
              {products.map((p) => (
                <div className="feat-slide" style={{ flex: `0 0 ${100 / perView}%`, maxWidth: `${100 / perView}%` }} key={p.id}>
                  <FeaturedCard product={p} />
                </div>
              ))}
            </div>
          </div>
          <button className="feat-nav next" onClick={goNext} aria-label={t('featuredSection.next')}>
            <FiChevronRight size={22} />
          </button>
          <div className="feat-dots">
            {Array.from({ length: pageCount }).map((_, i) => (
              <button key={i}
                className={`feat-dot ${i === index ? 'active' : ''}`}
                onClick={() => setIndex(i)}
                aria-label={`${t('featuredSection.goTo')} ${i + 1}`} />
            ))}
          </div>
        </div>
      ) : (
        <div className="feat-grid">
          {products.map((p, i) => (
            <motion.div key={p.id}
              initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-40px' }}
              transition={{ delay: Math.min(i * 0.08, 0.4), duration: 0.6, ease: [0.22, 1, 0.36, 1] }}>
              <FeaturedCard product={p} />
            </motion.div>
          ))}
        </div>
      )}

      <div className="feat-footer">
        <Link to="/prodotti" className="feat-viewall">
          {t('featuredSection.viewAll')} <FiArrowRight size={16} />
        </Link>
      </div>
    </section>
  );
};

export default FeaturedProducts;
